/**
 * Domain models and enums mirroring the backend Prisma schema
 * (factory-backend/prisma/schema.prisma).
 *
 * Wire-format notes:
 *  - Prisma `Decimal` values are serialised to JSON as **strings** — they are
 *    typed here as `string` and should be parsed with `toNumber()` before maths.
 *  - `DateTime` values arrive as ISO-8601 **strings**.
 */

// ───────────────────────────── Enums ─────────────────────────

export const Role = {
  ADMIN: "ADMIN",
  MANAGER: "MANAGER",
  STORE_KEEPER: "STORE_KEEPER",
  PRODUCTION_SUPERVISOR: "PRODUCTION_SUPERVISOR",
  CLIENT: "CLIENT",
} as const;
export type Role = (typeof Role)[keyof typeof Role];

export const ProductType = {
  RAW_MATERIAL: "RAW_MATERIAL",
  PACKAGING: "PACKAGING",
  FINISHED_GOOD: "FINISHED_GOOD",
} as const;
export type ProductType = (typeof ProductType)[keyof typeof ProductType];

export const UnitOfMeasure = {
  TON: "TON",
  KG: "KG",
  GRAM: "GRAM",
  PIECE: "PIECE",
  PACK: "PACK",
  CARTON: "CARTON",
  ROLL: "ROLL",
} as const;
export type UnitOfMeasure = (typeof UnitOfMeasure)[keyof typeof UnitOfMeasure];

export const StockMovementType = {
  OPENING: "OPENING",
  PURCHASE_IN: "PURCHASE_IN",
  PRODUCTION_ISSUE: "PRODUCTION_ISSUE",
  PRODUCTION_OUTPUT: "PRODUCTION_OUTPUT",
  SALES_DISPATCH: "SALES_DISPATCH",
  RETURN: "RETURN",
  ADJUSTMENT: "ADJUSTMENT",
} as const;
export type StockMovementType =
  (typeof StockMovementType)[keyof typeof StockMovementType];

export const StockDirection = { IN: "IN", OUT: "OUT" } as const;
export type StockDirection = (typeof StockDirection)[keyof typeof StockDirection];

export const OrderStatus = {
  PENDING: "PENDING",
  CONFIRMED: "CONFIRMED",
  PACKED: "PACKED",
  DISPATCHED: "DISPATCHED",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

export const PaymentMethod = {
  CASH: "CASH",
  BANK_TRANSFER: "BANK_TRANSFER",
  CARD: "CARD",
  CHEQUE: "CHEQUE",
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const PaymentStatus = {
  PENDING: "PENDING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  REFUNDED: "REFUNDED",
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const ProductionStatus = {
  DRAFT: "DRAFT",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
} as const;
export type ProductionStatus =
  (typeof ProductionStatus)[keyof typeof ProductionStatus];

export const Shift = {
  MORNING: "MORNING",
  EVENING: "EVENING",
  NIGHT: "NIGHT",
} as const;
export type Shift = (typeof Shift)[keyof typeof Shift];

export const SalaryStatus = {
  UNPAID: "UNPAID",
  PARTIAL: "PARTIAL",
  PAID: "PAID",
} as const;
export type SalaryStatus = (typeof SalaryStatus)[keyof typeof SalaryStatus];

export const SalaryTxnType = {
  SALARY: "SALARY",
  ADVANCE: "ADVANCE",
  OVERTIME: "OVERTIME",
  BONUS: "BONUS",
  DEDUCTION: "DEDUCTION",
} as const;
export type SalaryTxnType = (typeof SalaryTxnType)[keyof typeof SalaryTxnType];

export const NotificationType = {
  NEW_ORDER: "NEW_ORDER",
  ORDER_STATUS: "ORDER_STATUS",
  PAYMENT_RECEIVED: "PAYMENT_RECEIVED",
  LOW_STOCK: "LOW_STOCK",
  PRODUCTION_COMPLETED: "PRODUCTION_COMPLETED",
  SALARY_PAID: "SALARY_PAID",
  SYSTEM: "SYSTEM",
} as const;
export type NotificationType =
  (typeof NotificationType)[keyof typeof NotificationType];

// ──────────────────────────── Entities ───────────────────────

export interface ClientProfile {
  id: string;
  userId: string;
  companyName: string;
  contactPerson?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  taxNumber?: string | null;
  totalOrdered: string;
  totalPaid: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Computed `totalOrdered − totalPaid` on management responses. */
  remaining?: string;
  user?: {
    id: string;
    email: string;
    fullName: string;
    phone?: string | null;
    isActive?: boolean;
    lastLoginAt?: string | null;
  } | null;
}

export interface StaffProfile {
  id: string;
  userId?: string | null;
  employeeCode: string;
  fullName: string;
  designation?: string | null;
  department?: string | null;
  phone?: string | null;
  joiningDate: string;
  monthlySalary: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  phone?: string | null;
  role: Role;
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  client?: ClientProfile | null;
  staff?: StaffProfile | null;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { products: number };
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  categoryId: string;
  type: ProductType;
  unit: UnitOfMeasure;
  weightGrams?: string | null;
  packSize?: number | null;
  price: string;
  costPrice: string;
  stockQty: string;
  lowStockThreshold: string;
  imageUrl?: string | null;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  category?: Pick<Category, "id" | "name" | "slug"> | null;
}

export interface StockMovement {
  id: string;
  productId: string;
  type: StockMovementType;
  direction: StockDirection;
  quantity: string;
  balanceAfter: string;
  unitCost?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  note?: string | null;
  createdById?: string | null;
  createdAt: string;
  product?: Pick<Product, "id" | "name" | "sku" | "unit"> | null;
}

export interface Machine {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductionLine {
  id: string;
  runId: string;
  productId: string;
  quantity: string;
  product?: Pick<Product, "id" | "name" | "sku" | "unit"> | null;
}

export interface ProductionRun {
  id: string;
  runNumber: string;
  machineId?: string | null;
  supervisorId?: string | null;
  shift: Shift;
  status: ProductionStatus;
  totalInputQty: string;
  totalOutputQty: string;
  wastageQty: string;
  yieldPercent: string;
  notes?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  machine?: Machine | null;
  supervisor?: Pick<StaffProfile, "id" | "fullName" | "employeeCode"> | null;
  inputs?: ProductionLine[];
  outputs?: ProductionLine[];
}

export interface OrderItem {
  id: string;
  orderId: string;
  productId: string;
  productName: string;
  weightGrams?: string | null;
  unitPrice: string;
  quantity: string;
  lineTotal: string;
  product?: Pick<Product, "id" | "name" | "sku" | "unit"> | null;
}

export interface Order {
  id: string;
  orderNumber: string;
  clientId: string;
  status: OrderStatus;
  subtotal: string;
  taxAmount: string;
  totalAmount: string;
  notes?: string | null;
  placedAt: string;
  confirmedAt?: string | null;
  dispatchedAt?: string | null;
  deliveredAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
  client?:
    | (Pick<ClientProfile, "id" | "companyName"> & {
        user?: Pick<User, "id" | "fullName" | "email"> | null;
      })
    | null;
  items?: OrderItem[];
}

export interface Payment {
  id: string;
  paymentNumber: string;
  clientId: string;
  orderId?: string | null;
  amount: string;
  method: PaymentMethod;
  status: PaymentStatus;
  reference?: string | null;
  note?: string | null;
  paidAt: string;
  createdAt: string;
  updatedAt: string;
  client?:
    | (Pick<ClientProfile, "id" | "companyName"> & {
        user?: Pick<User, "id" | "fullName" | "email"> | null;
      })
    | null;
  order?: Pick<Order, "id" | "orderNumber" | "totalAmount"> | null;
}

export interface SalaryPayment {
  id: string;
  staffId: string;
  periodMonth: string;
  amountDue: string;
  amountPaid: string;
  status: SalaryStatus;
  paidAt?: string | null;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Server-computed `amountDue − amountPaid` on list responses. */
  remaining?: string;
  staff?:
    | (Pick<StaffProfile, "id" | "fullName" | "employeeCode"> & {
        department?: string | null;
      })
    | null;
}

/** One entry in a staff member's payment history (salary, advance, overtime…). */
export interface SalaryTransaction {
  id: string;
  staffId: string;
  type: SalaryTxnType;
  amount: string;
  periodMonth?: string | null;
  paidAt: string;
  note?: string | null;
  recordedById?: string | null;
  createdAt: string;
  updatedAt: string;
  staff?:
    | (Pick<StaffProfile, "id" | "fullName" | "employeeCode"> & {
        department?: string | null;
      })
    | null;
}

/** Lifetime ledger totals for a staff member, broken down by payment type. */
export interface StaffLedgerSummary {
  staffId: string;
  count: number;
  totalPaid: string;
  totalDeductions: string;
  netPaid: string;
  byType: Partial<Record<SalaryTxnType, string>>;
}

export interface AppNotification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: Record<string, unknown> | null;
  isRead: boolean;
  createdAt: string;
}

// ───────────────────── Computed / aggregate shapes ───────────────────

/** Inventory valuation = Σ(stockQty × costPrice), grouped by product type. */
export interface StockValuation {
  byType: Record<ProductType, string>;
  totalValue: string;
  productCount: number;
}

/** Trimmed product row returned by the stock report (`/inventory/stock`). */
export interface StockReportItem {
  id: string;
  sku: string;
  name: string;
  type: ProductType;
  unit: UnitOfMeasure;
  stockQty: string;
  costPrice: string;
  lowStockThreshold: string;
  category?: { id: string; name: string } | null;
  stockValue: string;
  isLow: boolean;
}

export interface LowStockItem {
  id: string;
  sku: string;
  name: string;
  unit: UnitOfMeasure;
  stockQty: string;
  lowStockThreshold: string;
  category?: { id: string; name: string } | null;
}

export interface ReceivablesSummary {
  totalOrdered: string;
  totalPaid: string;
  totalReceivable: string;
  clientCount: number;
}

export interface SalarySummary {
  periodMonth: string;
  totalDue: string;
  totalPaid: string;
  totalRemaining: string;
  recordCount: number;
}

export interface DashboardOverview {
  orders: {
    pending: number;
    today: { count: number; amount: string };
    byStatus: Array<{ status: OrderStatus; count: number; amount: string }>;
  };
  production: {
    completedThisMonth: number;
    averageYield: string;
    totalOutputKg: string;
    totalWastageKg: string;
  };
  stock: StockValuation;
  receivables: ReceivablesSummary;
  salaries: SalarySummary;
  generatedAt: string;
}

export interface ClientLedger {
  client: {
    id: string;
    companyName: string;
    contact?: { fullName: string; email: string } | null;
  };
  summary: { totalOrdered: string; totalPaid: string; remaining: string };
  orders: Array<
    Pick<Order, "id" | "orderNumber" | "status" | "totalAmount" | "placedAt">
  >;
  payments: Array<
    Pick<
      Payment,
      "id" | "paymentNumber" | "amount" | "method" | "status" | "paidAt" | "orderId"
    >
  >;
}

export interface SalesReport {
  period: { from: string; to: string };
  totals: { orderCount: number; totalSales: string };
  byStatus: Array<{ status: OrderStatus; count: number; amount: string }>;
  topProducts: Array<{
    product: { id: string; name?: string; sku?: string };
    quantity: string;
    revenue: string;
  }>;
}

export interface ProductionReport {
  period: { from: string; to: string };
  groupBy: "machine" | "shift";
  groups: Array<{
    key: string | { id: string; name?: string; code?: string };
    runs: number;
    averageYield: string;
    inputKg: string;
    outputKg: string;
    wastageKg: string;
  }>;
}

export interface DailySummary {
  date: string;
  ordersPlaced: { count: number; amount: string };
  ordersDispatched: number;
  production: { runsCompleted: number; outputKg: string; wastageKg: string };
  payments: { count: number; amount: string };
}

export interface PaymentReceipt {
  type: string;
  receiptNumber: string;
  issuedAt: string;
  client: { companyName: string; contact: string; email: string };
  payment: {
    amount: string;
    method: PaymentMethod;
    reference?: string | null;
    status: PaymentStatus;
  };
  order?: Pick<Order, "id" | "orderNumber" | "totalAmount"> | null;
}

export interface AuthSession {
  user: User;
  accessToken: string;
}
